import {
  DEFAULT_TIMELINE_LAYER_ID,
  MapTimelineLayer,
  MapTimelineLayerDetail,
  MapTimelineLayersDetails,
  TIMELINE_LAYER_IDS,
  TimelineLayerId,
} from 'features/mapping/mapping.constants';
import { useReleaseFlag } from 'hooks/useReleaseFlags';
import { useLightningLayerEnabled } from 'features/mapping/hooks/useLightningLayerEnabled';
import {
  LightningTimelineLookbackHours,
  useLightningTimelineLookbackHours,
} from 'features/mapping/hooks/useLightningTimelineLookbackHours';
import { useMemo } from 'react';

const lightningDetail = MapTimelineLayersDetails.lightning;

// The lightning details default to the 72h copy
const LIGHTNING_DETAIL_BY_LOOKBACK: Record<
  LightningTimelineLookbackHours,
  MapTimelineLayerDetail
> = {
  72: lightningDetail,
  48: {
    ...lightningDetail,
    subtitleKey: 'map.timelineLayers.lightning.subtitle48h',
    timelineLabelKeys: {
      ...lightningDetail.timelineLabelKeys,
      start: 'map.timelineLayers.lightning.timeline.start48h',
    },
  },
};

/** Timeline layers available to the user, in menu order. */
export const useTimelineLayerList = (): MapTimelineLayer[] => {
  const isTimelineLayersEnabled = useReleaseFlag(
    'show_timeline_layers_in_frontend',
  );
  const isSmokeForecastEnabled = useReleaseFlag(
    'show_smoke_forecast_layer_in_frontend',
  );
  const { showLightningLayer } = useLightningLayerEnabled();
  const lightningLookbackHours = useLightningTimelineLookbackHours();

  return useMemo(() => {
    if (!isTimelineLayersEnabled) {
      return [
        {
          id: DEFAULT_TIMELINE_LAYER_ID,
          ...MapTimelineLayersDetails[DEFAULT_TIMELINE_LAYER_ID],
        },
      ];
    }

    const isAvailable: Record<TimelineLayerId, boolean> = {
      radar: true,
      smoke: isSmokeForecastEnabled,
      lightning: showLightningLayer,
    };

    const details: Record<TimelineLayerId, MapTimelineLayerDetail> = {
      ...MapTimelineLayersDetails,
      lightning: LIGHTNING_DETAIL_BY_LOOKBACK[lightningLookbackHours],
    };

    return TIMELINE_LAYER_IDS.filter((id) => isAvailable[id]).map((id) => ({
      id,
      ...details[id],
    }));
  }, [
    isTimelineLayersEnabled,
    isSmokeForecastEnabled,
    showLightningLayer,
    lightningLookbackHours,
  ]);
};
